import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useCloudContext } from '../contexts/CloudProvider.jsx'
import { useAccessData } from '../hooks/useAccessData.js'
import LoadingSpinner from '../components/LoadingSpinner.jsx'
import EmailLinkWrapper from '../components/EmailLinkWrapper.jsx'

/**
 * DevicePage - Device landing page for email links
 * 
 * URL: /devices/:deviceId
 * Purpose: Direct device access from emails, shows device access page with navigation options
 * Used in: Access granted emails, device registration emails, device management emails
 */
function DevicePage() {
  const { deviceId } = useParams()
  const navigate = useNavigate()
  const { user, selectedDeviceId, selectDevice } = useCloudContext()
  const { useUserDevices } = useAccessData()
  
  console.error('DEBUG: DevicePage rendered, deviceId:', deviceId, 'URL:', window.location.href)
  
  const [state, setState] = useState({
    loading: true,
    error: null,
    device: null,
    deviceSelected: false,
  })

  const { data: userDevices, isLoading: devicesLoading } = useUserDevices()

  // Load device and check access
  useEffect(() => {
    if (!deviceId || !user || devicesLoading) return

    const checkDeviceAccess = async () => {
      try {
        setState(prev => ({ ...prev, loading: true, error: null }))
        
        // Check if user has access to this device
        const userDevice = userDevices?.find(device => device.id === deviceId)
        
        if (!userDevice) {
          // Device exists but user doesn't have access
          setState(prev => ({ 
            ...prev, 
            loading: false, 
            device: { id: deviceId, name: deviceId },
            error: 'You do not have access to this device.',
          }))
          return
        }
        
        setState(prev => ({ 
          ...prev, 
          loading: false, 
          device: userDevice,
        }))
        
        // Auto-select the device (but don't redirect for email landing pages)
        if (selectedDeviceId !== deviceId) {
          selectDevice(deviceId)
        }
        
        // For email landing pages, stay on this page instead of redirecting
        // Users can manually navigate to dashboard if needed
        setState(prev => ({ 
          ...prev, 
          device: userDevice,
          deviceSelected: true, // Track that we successfully selected the device
        }))
        
      } catch (error) {
        console.error('Error checking device access:', error)
        setState(prev => ({ 
          ...prev, 
          loading: false, 
          error: 'Failed to load device information. Please try again.',
        }))
      }
    }

    checkDeviceAccess()
  }, [deviceId, user, userDevices, devicesLoading, selectedDeviceId, selectDevice, navigate])

  const handleRequestAccess = useCallback(() => {
    // Navigate to add device page with serial parameter and request access state
    navigate(`/add-device?serial=${encodeURIComponent(deviceId)}`, { 
      state: { 
        requestAccessFor: deviceId,
        deviceName: state.device?.name,
      },
    })
  }, [navigate, deviceId, state.device?.name])

  const handleGoToDashboard = useCallback(() => {
    navigate('/dashboard', { replace: true })
  }, [navigate])

  // Loading state
  if (state.loading || devicesLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <div className="mt-3">
            <h5 className="mb-1">Loading Device...</h5>
            <p className="text-muted">Checking device access</p>
          </div>
        </div>
      </div>
    )
  }

  // Success state - device access confirmed and selected
  if (state.deviceSelected && state.device && !state.error) {
    return (
      <EmailLinkWrapper title="Device Access">
        <div className="container my-4">
          <div className="row justify-content-center">
            <div className="col-md-8 col-lg-6">
              <div className="card shadow-lg border-0">
                <div className="card-body p-4 text-center">
                  <div className="mb-3">
                    <i className="bi bi-check-circle text-success" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h4 className="mb-3">Device Access Confirmed</h4>
                  <p className="text-muted mb-4">
                    You now have access to <strong>{state.device?.name || deviceId}</strong>.
                  </p>
                  
                  {/* Device Info */}
                  <div className="bg-light rounded p-3 mb-4">
                    <div className="fw-semibold">{state.device.name || state.device.id}</div>
                    <small className="text-muted">Device ID: {state.device.id}</small>
                  </div>
                  
                  <div className="d-grid gap-2">
                    <button 
                      className="btn btn-primary"
                      onClick={handleGoToDashboard}
                    >
                      <i className="bi bi-speedometer2 me-2"></i>
                      Go to Dashboard
                    </button>
                    <button 
                      className="btn btn-outline-secondary"
                      onClick={() => navigate('/analysis')}
                    >
                      <i className="bi bi-graph-up me-2"></i>
                      View Analysis
                    </button>
                    <button 
                      className="btn btn-outline-secondary"
                      onClick={() => navigate('/configuration')}
                    >
                      <i className="bi bi-gear me-2"></i>
                      Device Settings
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </EmailLinkWrapper>
    )
  }

  // Error state
  if (state.error) {
    const isAccessDenied = state.error.includes('do not have access')
    
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <div className="card shadow-lg border-0">
                <div className="card-body p-4 text-center">
                  <div className="mb-3">
                    <i className={`bi ${isAccessDenied ? 'bi-lock' : 'bi-exclamation-triangle'} ${isAccessDenied ? 'text-warning' : 'text-danger'}`} style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h4 className="mb-3">
                    {isAccessDenied ? 'Access Required' : 'Device Problem'}
                  </h4>
                  <p className="text-muted mb-4">{state.error}</p>
                  
                  {/* Device Info (if available) */}
                  {state.device && (
                    <div className="bg-light rounded p-3 mb-4">
                      <div className="fw-semibold">{state.device.name || state.device.id}</div>
                      <small className="text-muted">Device ID: {state.device.id}</small>
                    </div>
                  )}
                  
                  <div className="d-grid gap-2">
                    {isAccessDenied && state.device ? (
                      <button 
                        className="btn btn-primary"
                        onClick={handleRequestAccess}
                      >
                        <i className="bi bi-person-plus me-2"></i>
                        Request Access
                      </button>
                    ) : null}
                    
                    <button 
                      className="btn btn-outline-secondary"
                      onClick={handleGoToDashboard}
                    >
                      <i className="bi bi-house me-2"></i>
                      Go to Dashboard
                    </button>
                    
                    {!isAccessDenied && (
                      <button 
                        className="btn btn-outline-secondary"
                        onClick={() => window.location.reload()}
                      >
                        <i className="bi bi-arrow-clockwise me-2"></i>
                        Try Again
                      </button>
                    )}
                  </div>
                  
                  {isAccessDenied && (
                    <div className="text-center mt-3">
                      <small className="text-muted">
                        Need help? Contact the device administrator or 
                        <a href="mailto:support@revatek.com" className="text-decoration-none ms-1">our support team</a>.
                      </small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // This shouldn't normally be reached
  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center">
      <div className="text-center">
        <h5>Unexpected state</h5>
        <button className="btn btn-primary mt-3" onClick={handleGoToDashboard}>
          Go to Dashboard
        </button>
      </div>
    </div>
  )
}

export default DevicePage
